import { useEffect, useState } from 'react';
import type { Logic } from '@/logic-engine';
import { hasPaymentMethodCondition, probePaymentMethod } from '@/player/sources/payment-method';
import { STRIPE_PUBLISHABLE_KEY } from '@/config/stripe-pk';

/**
 * F1d: async payment method probe — lazy, runs only when PAYMENT_METHOD
 * condition exists in logic (anti-pattern #4: Stripe.js must NOT load otherwise).
 *
 * Отдаёт [найденные способы оплаты, проверка завершена]. Плеер квиза держит
 * по второму значению прелоадер, пока условие стоит на первом экране.
 *
 * WEB-3402: вынесено из QuizScreenWizard.tsx без изменений поведения; вызов
 * стоит на прежнем месте порядка хуков плеера.
 */
export function useQuizPaymentProbe(
  logic: Logic,
  isPreview: boolean,
): readonly [string[], boolean] {
  const [paymentMethod, setPaymentMethod] = useState<string[]>([]);
  const [paymentMethodReady, setPaymentMethodReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // WEB-1336 (П1): отсечка предпросмотра — по образцу PaywallScreenWizard
    // (WEB-662 atom 2). Кабинет живёт одним долгим документом и пересобирает
    // предпросмотр многократно; ходить в js.stripe.com из предпросмотра
    // незачем, а зависшая проверка (упавший скрипт в existing-ветке) раньше
    // замораживала предпросмотр автора вечным индикатором.
    if (isPreview || !hasPaymentMethodCondition(logic)) {
      setPaymentMethodReady(true);
      return;
    }
    probePaymentMethod(STRIPE_PUBLISHABLE_KEY)
      .then((methods) => {
        if (!cancelled) setPaymentMethod(methods);
      })
      .catch(() => {
        // probePaymentMethod сам fail-open; этот catch — последний ремень.
      })
      .finally(() => {
        // WEB-1336 (П1): готовность снимается в ЛЮБОМ исходе — экран не
        // имеет права остаться за индикатором из-за проверки кошельков.
        if (!cancelled) setPaymentMethodReady(true);
      });
    return () => { cancelled = true; };
  }, [logic, isPreview]); // logic is stable after quiz loads; runs once

  return [paymentMethod, paymentMethodReady] as const;
}
